import { useMemo, useRef } from 'react'
import { useDataChannel } from '@livekit/components-react'

import { isZoomUrl } from './fallback'

/**
 * "Your class has moved to Zoom" — the fast half of a mid-lesson fallback.
 *
 * The server sends this into the room a moment before deleting it
 * (Meetings::SwitchProvider#broadcast_move), so the class learns where they are
 * going while they can still be told anything at all. Anyone who misses it is
 * not stranded: their client re-exchanges the instant the room disappears and
 * the 409 carries the same URL. This is the fast path, not the only one.
 *
 * WHY THIS IS NOT ON THE 'ischool-classroom' TOPIC
 * ------------------------------------------------
 * The LiveKit Ruby SDK's send_data does not expose the protocol's optional
 * `topic` field, so a server-sent packet arrives with no topic and the
 * topic-filtered subscription in useClassroomSignals would never see it. Hence a
 * second, topic-less subscription. There is still only ONE data channel —
 * passing a topic only filters, it does not open anything.
 *
 * THREE SECURITY GATES
 * --------------------
 * This message redirects children to an external site, so being wrong about its
 * provenance is not a cosmetic bug.
 *
 * 1. SERVER-SENT ONLY. A packet published by a participant carries that
 *    participant's identity; ours comes from the RoomService API and has none.
 *    Without this gate any student could publish a `class_moved` packet and send
 *    their whole class wherever they liked.
 * 2. ZOOM URLS ONLY. Belt and braces on the same path — see isZoomUrl.
 * 3. HOST LINKS ONLY FOR THE HOST. The server addresses the start_url to the
 *    tutor's identity, but addressing is the SERVER being right; this is the
 *    client refusing to act even if it isn't. A start_url in a student's hands
 *    is Zoom host control — mute, kick, end-meeting — over a room of children,
 *    so a host packet arriving at a non-host is dropped entirely rather than
 *    downgraded. They still get the room-wide join url, and the 409 backs that
 *    up if they somehow got neither.
 */

export interface ClassMoved {
  url: string
  /** True for the host link, which only the tutor is ever sent. */
  host: boolean
}

export function useClassMoved(isHost: boolean, onMoved: (moved: ClassMoved) => void): void {
  const decoder = useMemo(() => new TextDecoder(), [])
  /**
   * The server sends the host link first and then broadcasts the join link to
   * the room, so the tutor receives both. Once we hold a host URL we never
   * replace it with a join URL — that would silently demote the teacher to an
   * attendee in their own meeting. Order of arrival is not guaranteed, so this
   * is a latch rather than a sequence assumption.
   */
  const hostSeenRef = useRef(false)
  // Latest-ref: the callback is typically an inline arrow, and depending on it
  // would resubscribe the data channel on every parent render.
  const handler = useRef(onMoved)
  handler.current = onMoved

  useDataChannel((message) => {
    if (message.from) return // gate 1: participants cannot send this

    let signal: { kind?: unknown; url?: unknown; host?: unknown }
    try {
      const parsed: unknown = JSON.parse(decoder.decode(message.payload))
      // JSON.parse happily returns null, numbers and arrays. Reading .kind off
      // any of those throws, and a throw here takes the receive handler down for
      // the rest of the lesson — the same bug class useClassroomSignals was
      // hardened against.
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return
      signal = parsed as { kind?: unknown; url?: unknown; host?: unknown }
    } catch {
      return
    }

    if (signal.kind !== 'class_moved') return
    if (!isZoomUrl(signal.url)) return // gate 2

    const host = signal.host === true
    if (host && !isHost) return // gate 3
    if (hostSeenRef.current && !host) return
    if (host) hostSeenRef.current = true

    handler.current({ url: signal.url, host })
  })
}
